import { useEffect, useState } from 'react';

interface CharacterProps {
  x: number;
  isWalking: boolean;
  facingDirection: 'left' | 'right';
}

const Character = ({ x, isWalking, facingDirection }: CharacterProps) => {
  const [frame, setFrame] = useState(0);

  // Walking animation
  useEffect(() => {
    if (!isWalking) {
      setFrame(0);
      return;
    }

    const interval = setInterval(() => {
      setFrame(f => (f + 1) % 4);
    }, 150);

    return () => clearInterval(interval);
  }, [isWalking]);

  // Leg positions for animation frames
  const legOffsets = [
    { left: 4, right: 12 },  // Frame 0: neutral
    { left: 2, right: 14 },  // Frame 1: left forward
    { left: 4, right: 12 },  // Frame 2: neutral
    { left: 6, right: 10 },  // Frame 3: right forward
  ];

  const currentLegs = legOffsets[frame];

  return (
    <div
      className="absolute transition-transform"
      style={{
        left: `${x}px`,
        bottom: '10px',
        transform: `translateX(-50%) scaleX(${facingDirection === 'left' ? -1 : 1})`,
        zIndex: 30,
      }}
    >
      {/* Pixel art character using CSS */}
      <div
        className="relative"
        style={{
          width: '32px',
          height: '48px',
          imageRendering: 'pixelated',
        }}
      >
        {/* Shadow */}
        <div
          className="absolute bg-black rounded-full opacity-30"
          style={{
            bottom: '-4px',
            left: '4px',
            width: '24px',
            height: '8px',
          }}
        />

        {/* Head */}
        <div
          className="absolute bg-amber-200 rounded-full"
          style={{
            top: '0',
            left: '6px',
            width: '20px',
            height: '18px',
            boxShadow: 'inset -2px -2px 0 rgba(0,0,0,0.1)',
          }}
        >
          {/* Hair */}
          <div
            className="absolute bg-amber-900 rounded-t-full"
            style={{
              top: '-2px',
              left: '2px',
              width: '16px',
              height: '10px',
            }}
          />
          {/* Eyes */}
          <div
            className="absolute bg-gray-800 rounded-full"
            style={{ top: '8px', left: '4px', width: '3px', height: '3px' }}
          />
          <div
            className="absolute bg-gray-800 rounded-full"
            style={{ top: '8px', right: '4px', width: '3px', height: '3px' }}
          />
          {/* Smile */}
          <div
            className="absolute bg-rose-400"
            style={{
              bottom: '3px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '6px',
              height: '2px',
              borderRadius: '0 0 3px 3px',
            }}
          />
        </div>

        {/* Body (shirt) */}
        <div
          className="absolute bg-blue-500"
          style={{
            top: '18px',
            left: '4px',
            width: '24px',
            height: '16px',
            borderRadius: '4px 4px 0 0',
            boxShadow: 'inset -2px -2px 0 rgba(0,0,0,0.2)',
          }}
        >
          {/* Tie or collar detail */}
          <div
            className="absolute bg-red-500"
            style={{
              top: '2px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '4px',
              height: '12px',
              clipPath: 'polygon(50% 0%, 100% 20%, 80% 100%, 20% 100%, 0% 20%)',
            }}
          />
        </div>

        {/* Arms */}
        <div
          className="absolute bg-amber-200"
          style={{
            top: '20px',
            left: '0px',
            width: '6px',
            height: '12px',
            borderRadius: '3px',
            transform: isWalking ? `rotate(${frame % 2 === 0 ? 10 : -10}deg)` : 'none',
          }}
        />
        <div
          className="absolute bg-amber-200"
          style={{
            top: '20px',
            right: '0px',
            width: '6px',
            height: '12px',
            borderRadius: '3px',
            transform: isWalking ? `rotate(${frame % 2 === 0 ? -10 : 10}deg)` : 'none',
          }}
        />

        {/* Pants */}
        <div
          className="absolute bg-gray-700"
          style={{
            top: '34px',
            left: '6px',
            width: '20px',
            height: '6px',
          }}
        />

        {/* Legs */}
        <div
          className="absolute bg-gray-700"
          style={{
            top: '38px',
            left: `${currentLegs.left}px`,
            width: '6px',
            height: '10px',
            borderRadius: '0 0 2px 2px',
            transition: 'left 0.1s',
          }}
        />
        <div
          className="absolute bg-gray-700"
          style={{
            top: '38px',
            left: `${currentLegs.right}px`,
            width: '6px',
            height: '10px',
            borderRadius: '0 0 2px 2px',
            transition: 'left 0.1s',
          }}
        />

        {/* Shoes */}
        <div
          className="absolute bg-amber-800"
          style={{
            bottom: '0px',
            left: `${currentLegs.left - 1}px`,
            width: '8px',
            height: '4px',
            borderRadius: '2px',
            transition: 'left 0.1s',
          }}
        />
        <div
          className="absolute bg-amber-800"
          style={{
            bottom: '0px',
            left: `${currentLegs.right - 1}px`,
            width: '8px',
            height: '4px',
            borderRadius: '2px',
            transition: 'left 0.1s',
          }}
        />
      </div>
    </div>
  );
};

export default Character;
